/**
 * OTP policy helpers for the driver-assisted payment dialogue
 * (DRIVERAPP_OTP_SPEC.md).
 *
 * Two verification modes share one dialogue:
 *
 * - **client** (default, four digits) — AutoDispatch returns the code it texted, and
 *   the DriverApp compares locally, then assigns the contact with `confirmed:true`.
 *   This is the fleet's existing SMS behaviour.
 * - **server** (optional, six digits) — AD never returns the code. The entered string
 *   is submitted and AD decides. Enabled per fleet by `smsOtp.serverVerified`.
 *
 * Every function here treats the code as a STRING. `parseInt` would silently destroy
 * a leading-zero code ("0123" → 123) and would accept partial numeric text such as
 * "12ab", so it is never used on a code in this module or its callers.
 *
 * The policy is carried by the issued challenge, not assumed globally: the fleet flag
 * can change while a dialogue is open, and the challenge that was actually issued
 * stays authoritative until it is replaced.
 */

/** Default policy when the server sends none — the fleet's current SMS behaviour. */
export const DEFAULT_OTP_POLICY = Object.freeze({
  digits: 4,
  verification: 'client',
  expiresInSeconds: 300,
});

/**
 * Normalize an `otp` capability block or an issued challenge into a usable policy.
 *
 * Accepts both shapes because they carry the same three fields in different places:
 * the capability arrives nested (`{otp:{digits,verification}}` on state/lookup replies)
 * while an issued challenge carries them at its top level.
 *
 * Anything unrecognised falls back to the four-digit client default, which is the
 * documented behaviour for a fleet with no `smsOtp` configuration at all.
 */
export function resolveOtpPolicy(source) {
  const raw = source && typeof source === 'object'
    ? (source.otp && typeof source.otp === 'object' ? source.otp : source)
    : null;

  // Only 'server' opts into AD-side verification. An unknown/absent value stays client,
  // so a malformed reply can never silently disable local comparison.
  const verification = String(raw?.verification || '').toLowerCase() === 'server'
    ? 'server'
    : 'client';

  // Digits follow the mode unless the server states a sane explicit value. Server mode
  // is six digits and client mode four; a mismatched pair trusts the mode.
  const stated = Number(raw?.digits);
  const digits = Number.isInteger(stated) && stated >= 4 && stated <= 8
    ? stated
    : (verification === 'server' ? 6 : 4);

  const expires = Number(raw?.expiresInSeconds);
  const expiresInSeconds = Number.isFinite(expires) && expires > 0
    ? expires
    : DEFAULT_OTP_POLICY.expiresInSeconds;

  return { digits, verification, expiresInSeconds };
}

/** True when this policy asks AutoDispatch to verify the code rather than the app. */
export function isServerVerified(policy) {
  return resolveOtpPolicy(policy).verification === 'server';
}

/**
 * Keep only digits, capped at the policy length — the single sanitizer for code input.
 * Returning a string preserves leading zeroes through every later comparison.
 */
export function sanitizeOtpInput(value, policy) {
  const { digits } = resolveOtpPolicy(policy);
  return String(value ?? '').replace(/\D/g, '').slice(0, digits);
}

/** A code is submittable only at exactly the policy's length — never a partial entry. */
export function isOtpComplete(value, policy) {
  const { digits } = resolveOtpPolicy(policy);
  return sanitizeOtpInput(value, policy).length === digits;
}

/**
 * Client-mode comparison against the code AD returned.
 *
 * Both sides are padded to the policy width as strings, because AD's compatibility
 * value may arrive as the number 123 for the code "0123".
 *
 * Returns false in server mode: there is no local code to compare, and approving a
 * six-digit challenge locally would defeat the entire point of that mode.
 */
export function matchesOtpCode(entered, expected, policy) {
  const resolved = resolveOtpPolicy(policy);
  if (resolved.verification === 'server') return false;
  if (expected === null || expected === undefined || expected === '') return false;
  const left = sanitizeOtpInput(entered, resolved);
  if (left.length !== resolved.digits) return false;
  const right = String(expected).replace(/\D/g, '').padStart(resolved.digits, '0');
  return left === right;
}

/**
 * Normalize a phone number the way AutoDispatch's paymentContacts.phone() does:
 * ten North American digits, or eleven with a leading 1 (the 1 is dropped).
 *
 * Only a single leading 1 is removed — never an arbitrary run of ones, which would
 * corrupt a legitimate number beginning 11…. Returns null when the number cannot be
 * normalized, so the caller can report INVALID_PHONE without a round trip.
 */
export function normalizePhone(value) {
  let digits = String(value ?? '').replace(/\D/g, '');
  if (digits.length === 11 && digits[0] === '1') digits = digits.slice(1);
  return digits.length === 10 ? digits : null;
}

/** (123) 456-7890 for display. Falls back to the raw input when not normalizable. */
export function formatPhone(value) {
  const digits = normalizePhone(value);
  if (!digits) return String(value ?? '');
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/** Human label for a contact row, tolerating the server's blank-name "New User" case. */
export function contactLabel(contact) {
  const name = String(contact?.name ?? '').trim();
  return name || 'Unnamed contact';
}

/**
 * Driver-facing message for a documented dialogue error code.
 *
 * The server's own `error` text is preferred when present for codes whose wording may
 * carry detail; these are the fallbacks and the wording for codes that must read the
 * same however AD phrases them.
 */
export const OTP_ERROR_MESSAGES = Object.freeze({
  INVALID_PHONE: 'Enter a ten-digit phone number.',
  CONTACT_SELECTION_REQUIRED: 'More than one passenger uses this number. Select the right one.',
  CONTACT_UNAVAILABLE: 'That contact no longer matches this phone number. Search again.',
  SMS_DELIVERY_FAILED: 'The code could not be texted. Check the number and try again.',
  OTP_INVALID: 'That code is incorrect. Check with the passenger and try again.',
  OTP_EXPIRED: 'That code has expired. Send a new one.',
  PRICE_NOT_FINAL: 'The fare is not final yet, so payment cannot be collected.',
  ALREADY_PAID: 'This trip has already been paid.',
  PAYMENT_PENDING: 'A payment is already in progress for this trip.',
  NO_PAYMENT_REQUIRED: 'No payment is required for this trip.',
  PAYMENT_NOT_DUE: 'Payment is not due for this trip yet.',
  PAYMENT_OWNERSHIP_MISMATCH: 'This trip is no longer assigned to your vehicle.',
  PAYMENT_STATE_UNAVAILABLE: 'Dispatch could not read this trip\'s payment state. Try again.',
});

export function otpErrorMessage(result, fallback = 'Something went wrong. Try again.') {
  const code = result?.code;
  if (code && OTP_ERROR_MESSAGES[code]) return OTP_ERROR_MESSAGES[code];
  const text = String(result?.error || result?.message || '').trim();
  return text || fallback;
}

/**
 * Errors that mean the dialogue must stop and the caller must re-read payment state,
 * rather than letting the driver correct something and retry (§"Retry and navigation").
 */
export const OTP_TERMINAL_CODES = Object.freeze([
  'ALREADY_PAID',
  'PAYMENT_PENDING',
  'PRICE_NOT_FINAL',
  'NO_PAYMENT_REQUIRED',
  'PAYMENT_NOT_DUE',
  'PAYMENT_OWNERSHIP_MISMATCH',
]);

export function isTerminalOtpError(result) {
  return OTP_TERMINAL_CODES.includes(result?.code);
}
